import { type ApolloError, gql } from "@apollo/client";
import type { GetUiDefaultInput } from "../../../__generated__/base.gql";
import type { UiDefaultHookQuery } from "./__generated__/use-ui-default.gql";
import {
  type UiDefaultHookUpsertMutationOptions,
  useUiDefaultHookQuery,
  useUiDefaultHookUpsertMutation,
} from "./__generated__/use-ui-default.gql";

export const QUERY = gql`
  query uiDefaultHook($args: GetUiDefaultInput!) {
    UiDefault(input: $args) {
      id
      namespace
      name
      scope
      value
    }
  }
`;

gql`
  mutation uiDefaultHookUpsert($args: UpsertUiDefaultInput!) {
    upsertUiDefault(input: $args) {
      id
      namespace
      name
      scope
      value
    }
  }
`;

interface UseUiDefaultProps<UiDefaultValueType = unknown>
  extends GetUiDefaultInput {
  defaultValue?: UiDefaultValueType;
  onCompleted?: () => void;
  onError?: (error: ApolloError) => void;
}

interface UiDefaultReturn<UiDefaultValueType = unknown> {
  uiDefault: UiDefaultHookQuery["UiDefault"] | null;
  value: UiDefaultValueType;
  save: (
    value: UiDefaultValueType,
    options?: UiDefaultHookUpsertMutationOptions,
  ) => void;
  queryLoading: boolean;
  mutationLoading: boolean;
  error: ApolloError;
}

export default function useUiDefault<UiDefaultType = unknown>({
  name,
  namespace,
  scope,
  defaultValue,
  onCompleted,
  onError,
}: UseUiDefaultProps<UiDefaultType>): UiDefaultReturn<UiDefaultType> {
  const {
    data,
    loading: queryLoading,
    error,
  } = useUiDefaultHookQuery({
    variables: { args: { name, namespace, scope } },
  });

  const [upsertDefault, { loading: mutationLoading }] =
    useUiDefaultHookUpsertMutation({
      onCompleted,
      onError,
    });

  return {
    queryLoading,
    mutationLoading,
    error,
    uiDefault: data?.UiDefault,
    value:
      data && data?.UiDefault?.value !== undefined
        ? (data.UiDefault.value as UiDefaultType)
        : defaultValue,
    save: (
      value: UiDefaultType,
      options?: UiDefaultHookUpsertMutationOptions,
    ) =>
      upsertDefault({
        variables: { args: { name, namespace, scope, value } },
        ...options,
      }),
  };
}
